import { ElMessageBox, ElNotification } from "element-plus";
import { Customer, PostData, User, callHistory } from "~/types";
import { Tag } from "~/types/Tag";
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from "~/apis/requests/useApiCall";

import { useUsersStore } from "~/stores/usersStore";
import { useTicketsStore } from "~/projects/help-desk/stores/ticketsStore";
import {
  BulkActionResponse,
  BulkResolveResult,
  BulkUnpickResult,
} from "~/projects/help-desk/types/bulkAssign";
import { CallHistory } from "~/types/callHistory";
import { useTopBarBadgeStore } from "~/stores/topBarBadgeStore";
import { Profile } from "~/types/profile";
import { Referral } from "~/types/referral";
import MetaPage from "~/projects/help-desk/types/MetaPage";
import { Conversation, Ticket, TicketStatus } from "~/projects/help-desk/types";
import { TicketOwner } from "~/projects/help-desk/types/TicketOwner";
import { MetaSourceEvent } from "~/projects/help-desk/types/sourceEvents/metaSourceEvent";
import { MetaUser } from "~/projects/help-desk/types/meta/metaUser";
import { MetaPost } from "~/projects/help-desk/types/meta/metaPost";
import { Feedback } from "~/projects/help-desk/types/feedback";
import { ConversationCreatorType } from "~/projects/help-desk/types/enums/conversationCreatorType";
import { ConversationType } from "~/projects/help-desk/types/enums/conversationType";
import {
  DEFAULT_PROFILE_CONFLICT,
  type ProfileConflict,
} from "~/projects/help-desk/utils/profileConflict";

// Big enough that the first page overflows the list pane on a tall screen — a page
// that fits without a scrollbar gives the agent no way to ask for the next one.
const ASSIGNED_PAGE_SIZE = 15;

/** One page of the inbox list. `pinned_tickets` and `total` come with page 1 only. */
interface AssignedTicketsPage {
  tickets: Ticket[];
  pinned_tickets?: Ticket[];
  next_cursor: string | null;
  has_more: boolean;
  total: number | null;
}

class Store<T> {
  private store: Map<string | number, T> = new Map();

  get(key: string | number): T | null {
    return this.store.get(key) || null;
  }

  set(key: string | number, value: T) {
    this.store.set(key, value);
  }

  has(key: string | number): boolean {
    return this.store.has(key);
  }

  remove(key: string | number) {
    this.store.delete(key);
  }

  clear() {
    this.store.clear();
  }
}

/** Tickets currently being fetched to fill in a thin socket payload. */
const hydratingTicketIds = new Set<number>();

export const ticketsCache = new Store<Ticket>();
export const conversationsCache = new Store<Conversation[]>();
export const sourceEventCache = new Store<MetaSourceEvent>();

/**
 * Threads whose first fetch is still in flight, and any messages that arrived by
 * socket while it was.
 *
 * The conversations endpoint reads the thread server-side, so a message created
 * after that read is not in the response — but its broadcast can still reach this
 * browser before the response does. Assigning the response then erased it, and
 * because the cache is seeded from that same response the thread stayed short for
 * the rest of the session: the ticket card showed the new message (the list is
 * re-read from the server on every tab change) while the conversation pane never
 * did, and only a full page reload put it back. Anything that lands inside that
 * window is parked here and merged in when the fetch returns.
 */
const threadFetchesInFlight = new Set<number>();
const pendingConversations = new Store<Conversation[]>();

/** The platform's own timestamp where there is one, ours otherwise. */
const conversationTime = (conversation: Conversation): number =>
  new Date(
    conversation.original_created_at || conversation.created_at || 0,
  ).getTime();

/**
 * One thread, one merge rule: replace by id, otherwise append, then order by time
 * with the id as tie-break.
 *
 * Always a NEW array, never the one that went in. The DynamicScroller watches
 * `items` without `deep`, so an in-place push left a message in the data and out
 * of the DOM; and a fresh reference is also what lets MessageBox's
 * `_ticketMessages` watcher scroll the pane down to a message that just arrived —
 * a push never changed the reference, so that watcher had never once fired for a
 * socket message.
 */
export const mergeConversations = (
  base: Conversation[],
  incoming: Conversation[],
): Conversation[] => {
  const merged = [...base];

  incoming.forEach((conversation) => {
    if (!conversation?.id) return;

    const index = merged.findIndex((c) => c.id === conversation.id);
    if (index === -1) merged.push(conversation);
    else merged[index] = conversation;
  });

  return merged.sort(
    (a, b) =>
      conversationTime(a) - conversationTime(b) || (a.id ?? 0) - (b.id ?? 0),
  );
};
const metaPostRefreshes = new Map<number, Promise<boolean>>();
export const useInboxStore = definePiniaStore("inboxStore", {
  state: () => ({
    _selectedMessage: {
      tags: [] as Array<Tag>,
    },
    _selectedScrollTop: 0,
    _showNewMessageTagDialog: false,
    _assignedTickets: [] as Array<Ticket>,
    _ticketMessages: [] as Array<Conversation>,
    _selectedMessageAttachmentUpload: [], // To store attachments array
    _showAttachmentModal: false,
    _selectedTicket: {} as Ticket,
    _convForPrivateReply: {} as Conversation,
    _showAddTagDialog: false,
    _showLeadParserDialog: false,
    _showBlockTicketUserDialog: false,
    _showBrowseLinksDialog: false,
    _selectedTicketProfile: {} as Profile,
    _selectedTicketSourceEvent: {} as MetaSourceEvent,
    _ticketChannel: {} as MetaPage,
    _postInfo: {} as PostData,
    _filterData: {} as Partial<Ticket>,
    showFiltersModal: false,
    showAssignModal: false,
    showClientEditModal: false,
    _showUploadDialog: false,
    _showTicketsWithTask: false,
    _showHistoryModal: false,
    _showGalleryModal: false,
    _remarks: null as any,
    showInboxSection: "messages section",
    // The bulk selection, as ticket ids. This IS the request body: a row's
    // checkbox renders `_selectedIds.has(id)`, the count is `_selectedIds.size`,
    // and a bulk action sends `[..._selectedIds]`. One object read three ways, so
    // the tick, the number and the write cannot disagree — which is the whole
    // reason it can survive a tab change without ever describing a different set
    // of tickets than the one on screen.
    _selectedIds: new Set<number>(),
    // True while "Check all" is resolving its ids server-side. The row boxes and
    // the bulk buttons are disabled meanwhile, so nothing can fire against a
    // half-built set.
    _selectionResolving: false,
    // Bumped by clearSelection() and afterBulkAction(). An ids response carrying a
    // stale epoch is dropped, so a "Check all" the agent has since cleared cannot
    // land late and re-arm itself.
    _selectionEpoch: 0,
    // Rows un-ticked while a "Check all" was in flight. Subtracted when the
    // response lands, so a gesture made after the press is not silently undone.
    _selectionUnticked: new Set<number>(),
    // Which view the selection was made in ("All", "C.Waiting", "Favourites ·
    // filtered"). Shown in the selection bar so a selection carried across tabs
    // stays legible five clicks later.
    _selectionScopeLabel: "",
    _selectedTicketCallHistory: [] as Array<CallHistory>,
    shouldPauseAll: false,
    _showTicketsWithFavorites: false,
    _showPrivateReplyModal: false,
    _privateReplyModalData: {} as any,
    _showTicketWithCustomerWaiting: false,
    _showEditOrderForm: false,
    _selectedOrderForEdit: null as any,
    _showTicketWithAgentWaiting: false,
    _viewingTicketIds: [] as number[],
    _tabCounts: {
      all: 0,
      favorites: 0,
      hasTask: 0,
      customerwaiting: 0,
      agentwaiting: 0,
    },

    // ── Cursor pagination ────────────────────────────────────────────────────
    // The list is loaded a page at a time, ordered by (updated_at, id) DESC.
    // Tickets pinned by ?other_ticket= / ?selected= sit above it and are not part
    // of the paged stream, so they never move the cursor.
    _pinnedTickets: [] as Array<Ticket>,
    _assignedCursor: null as string | null,
    _assignedHasMore: false,
    _assignedLoading: false,
    _assignedLoadingMore: false,
    // Only the newest request may write to the list. Tab and filter clicks fire
    // faster than the network answers, and without this a stale response lands
    // rows belonging to a view the agent already left.
    _assignedRequestId: 0,
    // The querystring page 1 was fetched with, frozen so later pages cannot be
    // fetched against a filter set that has since changed underneath them.
    _assignedQuery: "",
    // The server's count for the whole filter, not the number of rows loaded.
    // null while searching, where the count costs as much as the page.
    _assignedTotal: null as number | null,

    // Bumped whenever a row is inserted or removed ABOVE the viewport, so the
    // list component can hold the scroll position steady. Appends never bump it:
    // they add below the fold and move nothing.
    _listPrependEpoch: 0,

    // Ticket id from ?selected= when the inbox was opened by a link or a
    // notification, so the server can pin it and the agent lands on it even if it
    // sits past the first page. Consumed by that first fetch and then cleared —
    // it is an arrival concern, not a filter.
    _deepLinkedTicketId: null as number | null,
  }),

  actions: {
    openFiltersModal() {
      this.showFiltersModal = true;
    },
    closeFiltersModal() {
      this.showFiltersModal = false;
    },
    openAssignModal() {
      this.showAssignModal = true;
    },
    closeAssignModal() {
      this.showAssignModal = false;
    },
    openClientEditModal() {
      this.showClientEditModal = true;
    },
    closeClientEditModal() {
      this.showClientEditModal = false;
    },
    pauseAllAudios() {
      this.shouldPauseAll = true;
      setTimeout(() => {
        this.shouldPauseAll = false;
      }, 0); // Reset the flag immediately after triggering
    },
    // api calls

    /**
     * The active tab + filters as plain params. Shared by the list request and by
     * "select all matching", so a bulk action can never act on a different set
     * than the one the agent is looking at.
     */
    assignedFilterParams(): Record<string, string> {
      const params: Record<string, string> = {
        has_task: String(this.$state._showTicketsWithTask),
        has_favorites: String(this.$state._showTicketsWithFavorites),
      };

      if (this.$state._showTicketWithCustomerWaiting) {
        params.last_reply = "customer";
      }
      if (this.$state._showTicketWithAgentWaiting) {
        params.last_reply = "agent";
      }

      for (const [key, value] of Object.entries(this._filterData)) {
        if (!value) continue;
        params[key] = String(value);
      }

      return params;
    },

    // One place that turns the current tab + filters into a querystring, so page 1
    // and every later page are guaranteed to describe the same result set.
    buildAssignedQuery(
      otherTicket: string | number | null,
      agentId: number | null = null,
    ): string {
      if (agentId === null) {
        agentId = useAuthStore().user?.id;
      }

      const params = new URLSearchParams(this.assignedFilterParams());

      if (otherTicket) {
        params.set("other_ticket", String(otherTicket));
      }

      // Pin a deep-linked ticket too: it may sit far down the stream, and without
      // this it would open in the reading pane while being absent from the list.
      if (this.$state._deepLinkedTicketId) {
        params.set("selected", String(this.$state._deepLinkedTicketId));
      }

      return `desk/users/${agentId}/tickets?${params.toString()}`;
    },

    /**
     * What a bulk action acts on: the selection, verbatim.
     *
     * No filter map travels with a bulk write any more, which is what makes the old
     * `tags` collision unreachable rather than merely avoided — `tags` in the
     * body is now unambiguously the tags to APPLY, because nothing here means
     * "tags to filter on". The server still supports the namespaced `filters`
     * form for the select_all path; this client simply never uses it.
     */
    bulkSelectionPayload(): Record<string, any> {
      return { ticket_ids: [...this.$state._selectedIds] };
    },

    setTabCount(count: number) {
      if (this.$state._showTicketsWithTask) {
        this.$state._tabCounts.hasTask = count;
      } else if (this.$state._showTicketsWithFavorites) {
        this.$state._tabCounts.favorites = count;
      } else if (this.$state._showTicketWithCustomerWaiting) {
        this.$state._tabCounts.customerwaiting = count;
      } else if (this.$state._showTicketWithAgentWaiting) {
        this.$state._tabCounts.agentwaiting = count;
      } else {
        this.$state._tabCounts.all = count;
      }
    },

    /**
     * First page. Resets the cursor and replaces the list.
     *
     * Callers use this as "reload the list", including after an action that did
     * not change the filters at all, so it always refetches. Switching panes on
     * mobile does not come through here — that unmounts the component but leaves
     * the store alone, so the pages already loaded survive on their own.
     */
    async getAssignedTickets(
      otherTicket: string | number | null,
      agentId: number | null = null,
    ) {
      const query = this.buildAssignedQuery(otherTicket, agentId);
      const requestId = ++this.$state._assignedRequestId;
      this.$state._assignedQuery = query;
      this.$state._assignedLoading = true;
      // The selection is deliberately NOT cleared here, and the bug that clearing
      // it fixed cannot come back. That bug was a DISAGREEMENT: ids resolved in
      // view A were still what the request sent, while the count fell to the loaded
      // row count and the ticks described view B. Now `ticket_ids`, the count and
      // every checkbox are one Set read three ways, so changing view cannot change
      // what the request would do without also changing every number and tick on
      // screen.
      //
      // Three things replace the reset, and all three ship with it: the selection
      // bar (count, originating view, Clear) that renders on every view while a
      // selection exists; a live checkbox on every loaded row whose id is in the
      // set, so each member is individually visible and removable wherever it
      // appears; and removeTicket(), which drops an id the moment its ticket leaves
      // the inbox, so the count follows reality.

      const { response, error } = await getRequest<AssignedTicketsPage>(
        `${query}&limit=${ASSIGNED_PAGE_SIZE}`,
      );

      // A newer tab/filter click already started; its answer owns the list.
      if (requestId !== this.$state._assignedRequestId) return;

      this.$state._assignedLoading = false;

      if (error || !response?.data) return;

      const page = response.data;

      this.$state._assignedTickets = page.tickets ?? [];
      this.$state._pinnedTickets = page.pinned_tickets ?? [];
      this.$state._assignedCursor = page.next_cursor ?? null;
      this.$state._assignedHasMore = Boolean(page.has_more);
      this.$state._assignedTotal = page.total ?? null;

      // Mark tickets as viewing if they match other_ticket IDs
      if (otherTicket) {
        this.$state._viewingTicketIds = String(otherTicket)
          .split(",")
          .map((id) => Number(id.trim()))
          .filter((id) => !isNaN(id));
      } else {
        this.$state._viewingTicketIds = [];
      }

      // The server total when we have it, otherwise what is on screen.
      this.setTabCount(
        this.$state._assignedTotal ?? this.$state._assignedTickets.length,
      );

      // Spend the deep link. It exists so that following a link lands on its
      // ticket even when that ticket sits past the loaded window — a one-time
      // arrival concern. Keeping it would pin that ticket to the top of every
      // later fetch, and since pinned rows deliberately ignore agent, status and
      // every filter, it would then show up under Favourites, Has-Task and the
      // waiting tabs without matching any of them — and without being counted,
      // because pinned rows never touch the total.
      this.$state._deepLinkedTicketId = null;
    },

    /**
     * Next page, appended. Uses push() rather than reassigning the array so no
     * loaded row changes index — which is what makes appending unable to disturb
     * the scroll position.
     */
    async loadMoreAssignedTickets() {
      if (!this.$state._assignedCursor || this.$state._assignedLoadingMore) {
        return;
      }

      const requestId = this.$state._assignedRequestId;
      this.$state._assignedLoadingMore = true;

      const { response, error } = await getRequest<AssignedTicketsPage>(
        `${
          this.$state._assignedQuery
        }&limit=${ASSIGNED_PAGE_SIZE}&cursor=${encodeURIComponent(
          this.$state._assignedCursor,
        )}`,
      );

      // Filters changed while this was in flight — these rows describe the old view.
      if (requestId !== this.$state._assignedRequestId) return;

      this.$state._assignedLoadingMore = false;

      if (error || !response?.data) return;

      const page = response.data;

      // A live event can hoist a row to the top while this page was in flight, so
      // the same ticket can arrive here as well. Drop what we already hold.
      const loaded = new Set(this.$state._assignedTickets.map((t) => t.id));
      const appended = (page.tickets ?? []).filter(
        (ticket) => !loaded.has(ticket.id),
      );
      this.$state._assignedTickets.push(...appended);

      // Nothing is ticked here on arrival. A row from page 2 renders ticked if and
      // only if its id is already in the selection — which it will be when the
      // agent pressed "Check all", because that resolved the whole filter to ids up
      // front rather than to the rows that happened to be loaded.

      this.$state._assignedCursor = page.next_cursor ?? null;
      this.$state._assignedHasMore = Boolean(page.has_more);
    },

    /**
     * The single way a ticket's checkbox state changes.
     *
     * There is no second array to drift out of step with any more: the id set IS
     * what the request sends, so un-ticking a row is the removal, not a note about
     * a removal. Every entry point still routes through here — the row checkbox,
     * ctrl+click on the card, and the watcher that prunes rows which stop being
     * selectable — so the gesture and the payload are the same edit.
     */
    setTicketChecked(id: number, checked: boolean) {
      if (checked) {
        this.$state._selectedIds.add(id);
        this.$state._selectionUnticked.delete(id);

        this.noteSelectionScope();
        return;
      }

      this.$state._selectedIds.delete(id);

      // A "Check all" may still be in flight. Without this its answer would put the
      // row straight back, silently undoing a gesture the agent made after pressing
      // it — the same class of bug as an untick that never became an exclusion.
      if (this.$state._selectionResolving) {
        this.$state._selectionUnticked.add(id);
      }

      if (this.$state._selectedIds.size === 0) {
        this.$state._selectionScopeLabel = "";
      }
    },

    /**
     * Record which view a ticket just joined the selection from.
     *
     * The bar names one view only while every member came from it. The moment a
     * ticket is added from somewhere else the label becomes "multiple views",
     * because naming just the first would be a claim about tickets that did not
     * come from there — the label is the only thing on screen explaining where a
     * selection that spans tabs came from, so it has to stop naming one when it
     * stops being true.
     *
     * The label is passed in by "Check all", which computed it when the button was
     * pressed: its request is asynchronous and the agent may have changed tab
     * before it lands, so recomputing here would name the wrong view.
     */
    noteSelectionScope(label?: string) {
      const scope = label ?? this.selectionScopeLabel();
      const current = this.$state._selectionScopeLabel;

      this.$state._selectionScopeLabel =
        !current || current === scope ? scope : "multiple views";
    },

    /**
     * A human name for the view a selection was made in.
     *
     * Derived from the params the list request actually carries, never from which
     * tab is highlighted: a ?last_reply= in the route is copied into _filterData and
     * overwrites the tab-derived value in assignedFilterParams, so the tab name can
     * be a lie and the params cannot.
     */
    selectionScopeLabel(): string {
      const params = this.assignedFilterParams();
      const parts: string[] = [];

      if (params.last_reply === "customer") parts.push("C.Waiting");
      if (params.last_reply === "agent") parts.push("A.Waiting");
      if (params.last_reply === "info") parts.push("Info");
      if (params.has_favorites === "true") parts.push("Favourites");
      if (params.has_task === "true") parts.push("Has Task");
      if (params.has_inbox_alert) parts.push("Alerts");

      if (!parts.length) parts.push("All");

      // Any other narrowing key — a label, a channel chip, a search — is worth
      // saying, because it is the difference between "All" and a slice of it.
      const narrowed = Object.entries(params).some(
        ([key, value]) =>
          ![
            "last_reply",
            "has_favorites",
            "has_task",
            "has_inbox_alert",
          ].includes(key) &&
          value !== "" &&
          value !== "false",
      );
      if (narrowed) parts.push("filtered");

      return parts.join(" · ");
    },

    /**
     * "Check all" — resolve the current filter to an explicit id list, once.
     *
     * The set that comes back IS the request. Nothing re-derives it afterwards,
     * which is what lets it survive a tab change without ever describing a
     * different set of tickets than the one on screen: on any view, a loaded row is
     * ticked exactly when its id is in here, and the count is the length of the
     * array the button will send.
     *
     * The endpoint runs assignedTicketsQuery — the same builder that paged the list
     * — so "select all 18" and the 18 rows the agent is looking at cannot come from
     * two different definitions of "in my inbox".
     */
    async resolveSelectionIds(agentId: number | null = null) {
      if (this.$state._selectionResolving) return;

      const targetId = agentId ?? useAuthStore().user?.id;
      const epoch = this.$state._selectionEpoch;
      const wasEmpty = this.$state._selectedIds.size === 0;
      const label = this.selectionScopeLabel();
      const params = new URLSearchParams(this.assignedFilterParams());

      this.$state._selectionResolving = true;
      this.$state._selectionUnticked = new Set<number>();

      const { response, error } = await getRequest<{ ids: number[] }>(
        `desk/users/${targetId}/ticket-ids?${params.toString()}`,
      );

      this.$state._selectionResolving = false;

      // Cleared, or settled by a bulk action, while this was in flight. These ids
      // describe a selection the agent has already abandoned.
      if (epoch !== this.$state._selectionEpoch) {
        this.$state._selectionUnticked = new Set<number>();
        return;
      }

      if (error || !response?.data) {
        // Leave the selection exactly as it was and say so. Falling back to the old
        // filter payload would put a number on screen that nothing had counted.
        this.$state._selectionUnticked = new Set<number>();
        ElMessageBox.alert(
          error?.response?.data?.message ||
            error?.message ||
            "Could not work out which tickets match this filter.",
          "Check All Failed",
          { type: "error", confirmButtonText: "OK" },
        );
        return;
      }

      // Anything un-ticked while the request was out stays un-ticked: that gesture
      // came after the press, so it is the later instruction.
      const unticked = this.$state._selectionUnticked;
      (response.data.ids ?? []).forEach((ticketId: number) => {
        if (!unticked.has(ticketId)) this.$state._selectedIds.add(ticketId);
      });
      this.$state._selectionUnticked = new Set<number>();

      // Same bookkeeping as a single tick, with the label captured at press time —
      // a second Check all from another view unions two scopes and the bar stops
      // naming just one. wasEmpty is why this cannot simply read the current label:
      // a press into an empty selection names its own view even if a stale label
      // were somehow left behind.
      if (wasEmpty) this.$state._selectionScopeLabel = "";
      this.noteSelectionScope(label);
    },

    /**
     * Empty the selection. Reachable from every view via the selection bar, which
     * is what makes a selection that outlives a tab change safe to have.
     */
    clearSelection() {
      this.$state._selectedIds = new Set<number>();
      this.$state._selectionUnticked = new Set<number>();
      this.$state._selectionScopeLabel = "";
      // Any "Check all" still in flight belongs to the selection just discarded.
      this.$state._selectionEpoch++;
    },

    async getTicketMessages(ticket: Ticket) {
      const { response, error } = await getRequest<Conversation[]>(
        `desk/tickets/${ticket.id}/conversations`,
      );
      if (!error) {
        // Through the same merge, and into the cache as well — a thread fetched by
        // one path and cached by another is how the two copies drift apart.
        const conversations = mergeConversations(response.data, []);
        conversationsCache.set(ticket.id, conversations);
        this.$state._ticketMessages = conversations;
        useTopBarBadgeStore().adjustMessageCount(
          -this.$state._selectedTicket.unread_count,
        );
        this.$state._selectedTicket.unread_count = 0;
      }
    },
    async getSelectedTicketProfile(profileId: number) {
      const url = `profiles/${profileId}`;
      const { response, error } = await getRequest<Object>(url);
      if (!error) {
        this.$state._selectedTicketProfile = response.data;
      }
    },
    async getTicketSourceEvent(ticket: Ticket) {
      // Start clean so the previous ticket's post preview never lingers.
      this.$state._selectedTicketSourceEvent = {} as MetaSourceEvent;

      // A ticket coming from a pick/update response can arrive without its
      // resolved source event (the update endpoint doesn't load it the way the
      // show endpoint does). Fall back to the full ticket so the comment's post
      // preview loads without needing a page refresh.
      if (!ticket?.source_event_id && ticket?.id) {
        const { response, error } = await getRequest<Ticket>(
          `desk/tickets/${ticket.id}?with=automation`,
        );
        if (!error && response?.data) {
          ticket = response.data;
          ticketsCache.set(ticket.id, ticket);
          if (this._selectedTicket?.id === ticket.id) {
            this.$state._selectedTicket = ticket;
          }
        }
      }

      if (!ticket?.source_event_id) return;

      const key = `${ticket.source_event_id}:${ticket.source_event_type}`;

      // Only trust a cached event that actually carries the post payload. A
      // stale/partial entry (e.g. cached before the post finished syncing) is
      // exactly why the preview stays blank until a hard refresh — which wipes
      // this in-memory cache — so re-fetch when the cached copy is incomplete.
      let event = sourceEventCache.get(key);
      if (!(event as any)?.meta_post) {
        const { response, error } = await getRequest<MetaSourceEvent>(
          `desk/source_events/${ticket.source_event_id}`,
        );
        if (!error && response?.data) {
          event = response.data;
          sourceEventCache.set(key, event);
        }
      }

      if (event) {
        this.$state._selectedTicketSourceEvent = event;
      }
    },
    // addTicketMessage only used for sending message from the ticket page
    async addTicketMessage(requestData: any) {
      const { response, error } = await postRequest<Conversation[]>(
        `desk/tickets/${this.$state._selectedTicket.id}/conversations`,
        requestData,
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to send message.";

        ElMessageBox.alert(message, "Error", {
          type: "error",
          confirmButtonText: "OK",
        });
        throw error;
      } else if (!error) {
        // Same merge as an arriving message: the send response and the broadcast
        // that may follow it are the same conversation, so it lands once.
        for (const conv of response.data) {
          this.upsertConversation(conv);
        }
        return response.data;
      }
    },
    // Send a WhatsApp template message (JSON, not multipart).
    async addTicketTemplateMessage(requestData: any) {
      const { response, error } = await postRequest<Conversation[]>(
        `desk/tickets/${this.$state._selectedTicket.id}/conversations`,
        requestData,
      );

      if (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to send template.";

        ElMessageBox.alert(message, "Error", {
          type: "error",
          confirmButtonText: "OK",
        });
        throw error;
      }

      for (const conv of response.data) {
        this.upsertConversation(conv);
      }
      return response.data;
    },
    async updateCurrentTicketOwner(owner: TicketOwner) {
      const userStore = useUsersStore();
      await userStore.updateUser(owner as User);
    },

    updateTicket(
      status: TicketStatus,
      remarks?: string,
      mentioned_users_id?: number[],
      category_id?: number,
      tags?: Tag[],
      agent_id?: number,
      ticket?: Ticket,
    ) {
      const payload: Record<string, any> = {
        status,
        mentioned_users_id,
        category_id,
        agent_id,
        tags,
      };

      const normalizedRemarks = (remarks || "").trim();

      if (normalizedRemarks && normalizedRemarks !== "<p><br></p>") {
        payload.remarks = remarks;
      }

      const returnedTicket = putRequest<Ticket>(
        `desk/tickets/${this._selectedTicket.id}`,
        payload,
      );

      if (ticket && ticketsCache.has(ticket.id)) {
        ticketsCache.set(ticket.id, ticket);
      }

      return returnedTicket;
    },
    pick(assignToProfile = false) {
      return useTicketsStore().pick(this._selectedTicket.id, assignToProfile);
    },
    unpick(
      remarks?: string,
      mentioned_users_id?: number[],
      category_id?: number | null,
      transfer_to_agent_id?: number | null,
      assignToProfile = false,
      onProfileConflict: ProfileConflict = DEFAULT_PROFILE_CONFLICT,
      tags?: number[],
    ) {
      return useTicketsStore().unpick(this._selectedTicket.id, {
        ...this.ticketNotePayload(
          remarks,
          mentioned_users_id,
          category_id,
          tags,
        ),
        transfer_to_agent_id: transfer_to_agent_id || undefined,
        ...(assignToProfile
          ? {
              assign_to_profile: true,
              on_profile_conflict: onProfileConflict,
            }
          : {}),
      });
    },
    resolve(
      remarks?: string,
      mentioned_users_id?: number[],
      category_id?: number | null,
      tags?: number[],
    ) {
      return useTicketsStore().resolve(
        this._selectedTicket.id,
        this.ticketNotePayload(remarks, mentioned_users_id, category_id, tags),
      );
    },
    ticketNotePayload(
      remarks?: string,
      mentioned_users_id?: number[],
      category_id?: number | null,
      tags?: number[],
    ) {
      const payload: Record<string, any> = {
        mentioned_users_id,
        category_id,
        tags,
      };

      const normalizedRemarks = (remarks || "").trim();

      if (normalizedRemarks && normalizedRemarks !== "<p><br></p>") {
        payload.remarks = remarks;
      }

      return payload;
    },
    async uploadFile(formData: FormData) {
      return await postRequest("resources", formData);
    },
    // async syncMetaPost(metaPostId: number) {
    //   const { response, error } = await putRequest<MetaSourceEvent>(
    //     `desk/meta/facebook/post/${metaPostId}`,
    //     {},
    //   );
    //   if (!error) return response.data;
    //   return null;
    // },
    async refreshMetaPost(metaPostId: number) {
      if (!metaPostId) return false;

      const pending = metaPostRefreshes.get(metaPostId);
      if (pending) return await pending;

      // Snapshot the asking ticket: the endpoint makes a synchronous Graph call, and
      // an agent who moves on during it would otherwise get this post merged into
      // whichever ticket is open when the response lands — and cached under its key.
      const ticket = this.$state._selectedTicket;

      const refresh = (async () => {
        const { response, error } = await putRequest<MetaPost>(
          `desk/meta/facebook/post/${metaPostId}`,
          {},
        );

        // A stale preview still shows the message and the permalink, so a failed
        // refresh has to leave it alone rather than overwrite it with nothing.
        if (error || !response?.data) return false;

        if (this.$state._selectedTicket?.id !== ticket?.id) return false;

        const event = {
          ...this.$state._selectedTicketSourceEvent,
          meta_post: response.data,
        } as MetaSourceEvent;

        this.$state._selectedTicketSourceEvent = event;

        // The cache is otherwise only written on the GET path, so without this it
        // keeps the expired links and reinstalls them on the next ticket visit.
        if (ticket?.source_event_id) {
          sourceEventCache.set(
            `${ticket.source_event_id}:${ticket.source_event_type}`,
            event,
          );
        }

        return true;
      })();

      metaPostRefreshes.set(metaPostId, refresh);

      return await refresh;
    },
    async triggerAttachmentSync(convId: number, convertAudio = false) {
      const query = convertAudio ? "?convertAudio=1" : "";
      await postRequest(
        `desk/conversations/${convId}/triggerAttachmentSync${query}`,
        {},
      );
      return null;
    },
    async hideConversation(conversationId: number, isHidden: boolean) {
      const payload = { is_hidden: isHidden };
      const { response, error } = await postRequest(
        `desk/conversations/${conversationId}/hide-unhide`,
        payload,
      );

      if (error) {
        ElNotification({
          type: "error",
          title: "Failed",
          message: "Attempt Failed",
        });
      } else if (!error) {
        ElNotification({
          title: "Success",
          message: isHidden
            ? "Comment successfully hidden."
            : "Comment successfully unhidden.",
          type: "success",
        });
      }
      return response;
    },

    async deleteConversation(conversationId: number) {
      return await deleteRequest(`desk/conversations/${conversationId}`);
    },
    async updateConversation(formData: number) {
      const { response, error } = await putRequest<Conversation>(
        `desk/conversations/${formData.id}`,
        {
          tag_ids: formData.tags,
        },
      );
      if (!error) {
        this.setSelectedTicket(this.$state._selectedTicket.id);
      }
      return null;
    },
    async addAttachment(profileId: number) {
      const payload = {
        attachable_id: profileId,
        attachable_type: "Profile",
        attachments: this._selectedMessageAttachmentUpload.map(
          (attachment) => ({
            name: attachment.name,
            description: attachment.description,
            url: attachment.payload.url,
          }),
        ),
      };

      const { response, error } = await postRequest(
        `attachments/upload-from-url`,
        payload,
      );

      if (error) {
        const responseData = error?.response?.data;

        let message =
          responseData?.message ||
          responseData?.error ||
          "Something went wrong";

        if (responseData?.errors) {
          const attachmentErrors: string[] = [];

          for (const key in responseData.errors) {
            if (/^attachments\.\d+\.name$/.test(key)) {
              // Convert attachments.0.name => attachment 1 name
              const index = parseInt(key.match(/\d+/)![0], 10) + 1;
              responseData.errors[key].forEach((err: string) => {
                attachmentErrors.push(
                  err.replace(
                    `attachments.${index - 1}.name`,
                    `attachment ${index} name`,
                  ),
                );
              });
            }
          }

          if (attachmentErrors.length > 0) {
            // Join all messages with a line break
            message = attachmentErrors.join("<br/>");
          }
        }

        ElMessageBox({
          title: "Failed",
          message,
          type: "error",
          dangerouslyUseHTMLString: true,
        });

        return;
      } else if (!error) {
        this._showAttachmentModal = false;
        ElNotification({
          title: "Attachment Added",
          message: `Attachment added to Profile: ${profileId}`,
          type: "success",
        });
      }
      return response;
    },

    async addFeedback(formData: Feedback) {
      const { response, error } = await postRequest<Feedback>(
        `desk/tickets/${this.$state._selectedTicket.id}/feedback`,
        formData,
      );
      if (!error) return response.data;
      return null;
    },
    async toggleFavorite(ticketId: number) {
      const { response, error } = await putRequest<boolean>(
        `desk/tickets/${ticketId}/toggleFavorite`,
      );
      if (!error) {
        const ticket = this._assignedTickets.find(
          (ticket) => ticket.id === ticketId,
        );
        if (ticket) {
          ticket.has_favorited = !ticket.has_favorited;
        }
      }
    },
    async blockUnblockThisProfile(
      ticketOwnerId: number,
      is_blocked: boolean,
      reason: string | null,
    ) {
      const url = `desk/meta-users/${ticketOwnerId}/block-unblock`;

      const payload = {
        blocked: is_blocked,
        ticket_id: ticketOwnerId,
        block_reason: reason,
      };

      const { response, error } = await postRequest(url, payload);

      if (error) {
        ElMessageBox({
          title: "Failed",
          message:
            error?.response?.data?.message || error?.response?.data?.error,
          type: "error",
        });
        return;
      }

      ElNotification({
        type: "success",
        title: "Attempt Successful",
      });
      return { response };
    },

    // mutations
    async unreadTicket(ticketId: number) {
      const { response, error } = await putRequest<boolean>(
        `desk/tickets/${ticketId}/reset-unread-count`,
        {},
      );
      if (error) {
        ElNotification({
          type: "error",
          title: "Reset Unread Count Failed",
          message:
            error.response?.data?.message ||
            error.response.data?.error ||
            "Failed to reset unread count",
        });
      }
    },
    async setSelectedTicket(id: number, ticketParam: Ticket | null = null) {
      this._selectedTicketCallHistory = [];
      this.setSelectedTicketProfile({});
      let ticket;
      if (!ticketParam) {
        ticket = ticketsCache.get(id);
      } else {
        ticket = ticketParam;
      }
      if (!ticket) {
        const { response, error } = await getRequest<Ticket>(
          `desk/tickets/${id}?with=automation`,
        );
        if (!error) {
          ticket = response.data;
          ticketsCache.set(id, ticket);
        }
      }
      if (ticket) {
        // Whether we are about to fetch the thread decides how the badge is cleared:
        // the conversations endpoint zeroes unread_count server-side itself, so
        // calling reset-unread-count as well would write the ticket row twice for one
        // ticket open — and each write now broadcasts a ticket_updated.
        const cachedConversations = conversationsCache.get(ticket.id);

        if (ticket.unread_count > 0) {
          if (cachedConversations) this.unreadTicket(ticket.id);
          useTopBarBadgeStore().adjustMessageCount(-ticket.unread_count);
          ticket.unread_count = 0;
          ticketsCache.set(ticket.id, ticket);
        }

        if (ticket.inbox_alert && ticket.agent_id === useAuthStore().user?.id) {
          if (cachedConversations) {
            getRequest<Conversation[]>(
              `desk/tickets/${ticket.id}/conversations`,
            );
          }
          ticket.inbox_alert = null;
          ticketsCache.set(ticket.id, ticket);
        }
        // Sync onto the list item ONLY the fields the list endpoint omits
        // (tags — the relation; and the freshest category_id). Patch in place
        // so list-owned fields (has_favorited, last_conversation, status, owner,
        // channel, source) keep their live values instead of being clobbered by
        // a possibly-stale cache/detail payload.
        const assignedIndex = this._assignedTickets.findIndex(
          (t) => t.id === ticket.id,
        );
        if (assignedIndex !== -1) {
          const existing = this._assignedTickets[assignedIndex];
          this._assignedTickets[assignedIndex] = {
            ...existing,
            // Keep existing tags if this payload doesn't carry them (e.g. the
            // pick response); an empty array ([] = cleared) is still respected.
            tags: ticket.tags ?? existing.tags,
            category_id: ticket.category_id,
            unread_count: 0,
            inbox_alert: ticket.inbox_alert ?? null,
          };
        }
        this.$state._remarks = ticket.remarks;
        this.$state._selectedTicket = ticket;
        let conversations = cachedConversations;
        if (!conversations) {
          // Declare the fetch BEFORE awaiting it. The server reads the thread when
          // the request arrives, so a message broadcast while the response is in
          // flight cannot be in it — and the assignment below would then wipe it
          // out, permanently, since the cache is seeded from the same response.
          // upsertConversation parks anything that lands in this window.
          threadFetchesInFlight.add(ticket.id);
          try {
            const { response, error } = await getRequest<Conversation[]>(
              `desk/tickets/${ticket.id}/conversations`,
            );
            if (!error) {
              conversations = mergeConversations(
                response.data,
                pendingConversations.get(ticket.id) ?? [],
              );
              conversationsCache.set(ticket.id, conversations);
            }
          } finally {
            threadFetchesInFlight.delete(ticket.id);
            pendingConversations.remove(ticket.id);
          }
        }
        // The agent may have clicked on to another ticket while we waited. Painting
        // this thread now would put one ticket's messages under another's header.
        if (conversations && this._selectedTicket?.id === ticket.id) {
          this.$state._ticketMessages = conversations;
        }
        if (ticket.owner?.profile) {
          this.setSelectedTicketProfile(ticket.owner.profile);
        }
        // Removed the call to getTicketSourceEvent here
      }
    },
    async getSelectedTicketCallHistory(phone: number) {
      const { response, error } = await getRequest<CallHistory[]>(
        `cc/agent-call-histories?length=5&dir=desc&with=agent&without=tasks,category,sub_category,sub_sub_category,closing_status,brand&status=finished&customer_mobile_no=${phone}`,
      );
      if (!error) {
        this.$state._selectedTicketCallHistory = response.data;
      }
    },
    /**
     * The outcome goes back to the caller rather than to a toast: the modal that
     * fired the run reports it, where the agent is still looking.
     */
    async bulkResolve(
      data?: Object,
    ): Promise<BulkActionResponse<BulkResolveResult>> {
      const { response, error } = await putRequest<BulkResolveResult>(
        "desk/tickets/bulk-resolve",
        { ...this.bulkSelectionPayload(), ...data },
      );
      if (!error) {
        const resolved = response.data?.resolved_ids ?? [];
        this.removeTickets(resolved);
        // Keep whatever the server would not touch still ticked, so a skipped
        // ticket stays in front of the agent. Matches bulkUnpick: dropping down to
        // only the *explained* skips would silently unselect the rest.
        this.afterBulkAction(resolved);
        return { result: response.data, handledIds: resolved };
      }

      // The refusal body is worth keeping: a 422 from bulk-unpick still names the
      // tickets it would not touch, and the modal renders those beside the error.
      return {
        result: error.response?.data ?? null,
        handledIds: [],
        errorMessage:
          error.response?.data?.message ||
          error.message ||
          "Failed to resolve the selected tickets.",
      };
    },
    async bulkUnpick(
      data?: Object,
    ): Promise<BulkActionResponse<BulkUnpickResult>> {
      const { response, error } = await putRequest<BulkUnpickResult>(
        "desk/tickets/bulk-unpick",
        { ...this.bulkSelectionPayload(), ...data },
      );
      if (!error) {
        const unpicked = response.data?.unpicked_ids ?? [];
        this.removeTickets(unpicked);
        this.afterBulkAction(unpicked);
        return { result: response.data, handledIds: unpicked };
      }

      return {
        result: error.response?.data ?? null,
        handledIds: [],
        errorMessage:
          error.response?.data?.message ||
          error.message ||
          "Failed to unpick the selected tickets.",
      };
    },
    /**
     * Settle the selection after a bulk run: drop what the server handled, keep
     * what it refused.
     *
     * Only the handled ids leave. Anything skipped — a task-blocked resolve, a
     * ticket that was no longer the owner's latest — stays selected and ticked, so
     * it is still in front of the agent to act on. Nothing new is ever ticked by an
     * action completing; the old version re-ticked every still-loaded row after a
     * select-all, manufacturing a selection over tickets nobody had chosen.
     */
    afterBulkAction(handledIds: number[]) {
      handledIds.forEach((id) => this.$state._selectedIds.delete(id));

      if (this.$state._selectedIds.size === 0) {
        this.$state._selectionScopeLabel = "";
      }

      // A "Check all" still in flight was armed against the pre-action list.
      this.$state._selectionEpoch++;
    },
    async sendPrivateReply(formData: any) {
      const { response, error } = await postRequest<any>(
        `desk/conversations/${this.$state._convForPrivateReply.id}/private`,
        formData,
      );
      if (error) {
        ElMessageBox({
          type: "error",
          title: "Private Reply Failed",
          message: error.response?.data?.message || error.message,
        });
        return null;
      }

      return response.data;
    },
    async sendMessageForPrivateReply() {
      if (!this.privateReplyModalData.sendToComment) return;
      const requestData = {
        body: this.privateReplyModalData.commentText,
        creator_type: ConversationCreatorType.USER,
        creator_id: useAuthStore().user.id,
        unique_id: `${Date.now()}`,
        reply_to_id: this.$state._convForPrivateReply?.id,
      };
      this.addTicketMessage(requestData);
    },

    setSelectedMessageAttachments(attachmentsJson) {
      try {
        this._selectedMessageAttachmentUpload =
          typeof attachmentsJson === "string"
            ? JSON.parse(attachmentsJson)
            : attachmentsJson;

        this._showAttachmentModal = true;
      } catch (e) {
        this._selectedMessageAttachmentUpload = [];
      }
    },

    closeAttachmentModal() {
      this._showAttachmentModal = false;
    },
    setBlockTicketOwnerDIalog(data: any) {
      this._showBlockTicketUserDialog = data;
    },
    setPrivateReplyModalData(data: any) {
      this._privateReplyModalData = data;
    },
    setConvForPrivateReply(conv: Conversation) {
      this._convForPrivateReply = conv;
    },
    setPrivateReplyModalVisibility(show: boolean) {
      this._showPrivateReplyModal = show;
    },
    resetSelectedTicket() {
      this._selectedTicket = {} as Ticket;
    },
    // addTicketMessageFromEvent not being used anywhere
    addTicketMessageFromEvent(conversation: Conversation) {
      this._ticketMessages.push(conversation);
    },
    removeSelectedTicket() {
      this.removeTicket(this._selectedTicket.id);
    },
    replaceTicketConversation(index: number, conversation: Conversation) {
      this._ticketMessages.splice(index, 1, conversation);
    },
    setShowUploadDialogVisibility(show: boolean) {
      this._showUploadDialog = show;
    },
    setShowAddTagDialogVisibility(show: boolean) {
      this._showAddTagDialog = show;
    },
    setShowLeadParserDialogVisibility(show: boolean) {
      console.log("parser show", show);
      this._showLeadParserDialog = show;
    },
    setShowBrowseLinksDialogVisibility(show: boolean) {
      this._showBrowseLinksDialog = show;
    },
    setCurrentSourceEvent(sourceEvent: MetaSourceEvent) {
      this._selectedTicketSourceEvent = sourceEvent;
    },
    setFilterData(filterData: object) {
      this._filterData = filterData;
    },
    setDeepLinkedTicketId(ticketId: number | null) {
      this.$state._deepLinkedTicketId =
        ticketId && !isNaN(ticketId) ? ticketId : null;
    },

    /**
     * The one way a conversation enters a thread — created or updated, by socket or
     * by our own send.
     *
     * It used to be three: the component wrote a new array into the cache, pushed
     * in place into `_ticketMessages`, and sorted one of the two but not the other.
     * Two copies of the same thread kept by two different mechanics can disagree,
     * and the copy that ends up wrong is the cache — which setSelectedTicket then
     * trusts for the rest of the session.
     *
     * Updates upsert rather than replace-if-present: a delivery receipt for a
     * message this browser never received now repairs the thread instead of being
     * dropped on the floor.
     */
    upsertConversation(conversation: Conversation) {
      const ticketId = conversation?.ticket_id;
      if (!ticketId || !conversation?.id) return;

      // Its thread is being fetched right now and the response predates this
      // message. Park it and stop: the fetch merges it in, and until it lands
      // `_ticketMessages` still holds whichever thread the agent came from — adding
      // to that would show one ticket's message under another ticket's header. A
      // fetch only runs when there is no cached thread, so there is nothing else
      // here to update either.
      if (threadFetchesInFlight.has(ticketId)) {
        pendingConversations.set(
          ticketId,
          mergeConversations(pendingConversations.get(ticketId) ?? [], [
            conversation,
          ]),
        );
        return;
      }

      // Only ever extend a thread we already hold in full. Seeding the cache from a
      // single message would make setSelectedTicket serve that one message as the
      // whole conversation.
      const cached = conversationsCache.get(ticketId);
      if (cached) {
        conversationsCache.set(
          ticketId,
          mergeConversations(cached, [conversation]),
        );
      }

      if (ticketId === this._selectedTicket?.id) {
        this.$state._ticketMessages = mergeConversations(this._ticketMessages, [
          conversation,
        ]);
      }
    },
    // Read receipts arrive per ticket (not per message): the customer has read
    // every agent (outgoing) message sent up to `readAt`. Stamp `read_at` on
    // those conversations so the outgoing bubbles flip to the double tick.
    // Returns fresh objects/arrays so Vue re-renders the affected bubbles.
    markTicketConversationsRead(ticketId: number, readAt: string) {
      const readTime = new Date(readAt).getTime();

      const applyRead = (conversations: Conversation[]): Conversation[] => {
        let changed = false;
        const updated = conversations.map((conv) => {
          const createdAt = conv.original_created_at || conv.created_at;
          const isOutgoing = conv.creator_type === ConversationCreatorType.USER;
          const isUpToReadPoint =
            !createdAt || new Date(createdAt).getTime() <= readTime;

          if (isOutgoing && isUpToReadPoint && conv.read_at !== readAt) {
            changed = true;
            return { ...conv, read_at: readAt };
          }
          return conv;
        });

        return changed ? updated : conversations;
      };

      // Patch the cached thread (present when the ticket has been opened before).
      const cached = conversationsCache.get(ticketId);
      if (cached) {
        const next = applyRead(cached);
        if (next !== cached) conversationsCache.set(ticketId, next);
      }

      // Patch the live thread when this ticket is currently open.
      if (ticketId === this._selectedTicket?.id) {
        const next = applyRead(this._ticketMessages);
        if (next !== this._ticketMessages) this._ticketMessages = next;
      }
    },
    setShowTicketsWithCustomerWaiting(show: boolean) {
      this._showTicketWithCustomerWaiting = show;
    },
    setShowTicketsWithAgentWaiting(show: boolean) {
      this._showTicketWithAgentWaiting = show;
    },
    setSelectedMessage(message: Object) {
      this._selectedMessage = message;
    },
    setSelectedTicketAudioModalVisibility(show: boolean) {
      console.log("show");
      this._selectedTicketShowAudioModal = show;
    },
    setNewMessageTagDialogVisibility(show: boolean) {
      this._showNewMessageTagDialog = show;
    },
    setShowTicketsWithTask(value: boolean) {
      this._showTicketsWithTask = value;
    },
    setSelectedTicketProfile(profile: any) {
      this._selectedTicketProfile = profile;
    },
    setShowHistoryModalVisibility(show: boolean) {
      this._showHistoryModal = show;
    },
    setGalleryModalVIsibility(show: boolean) {
      this._showGalleryModal = show;
    },
    // A real-time ticket_assigned event can arrive for any channel; only surface
    // it in the assigned list when it matches the active channel/source
    // quick-filter (the chips patch source_platform / source_type here).
    doesTicketPassFilter(ticket: Ticket): boolean {
      const filter = this._filterData as any;

      if (
        filter.source_platform &&
        ticket.source_platform !== filter.source_platform
      ) {
        return false;
      }
      if (filter.source_type && ticket.source_type !== filter.source_type) {
        return false;
      }
      if (
        filter.channel_id &&
        Number(ticket.channel_id) !== Number(filter.channel_id)
      ) {
        return false;
      }
      if (filter.channel_type && ticket.channel_type !== filter.channel_type) {
        return false;
      }
      if (
        filter.category &&
        Number(ticket.category_id) !== Number(filter.category)
      ) {
        return false;
      }
      if (filter.unread && !(ticket.unread_count >= 1)) {
        return false;
      }
      if (
        filter.has_inbox_alert &&
        String(filter.has_inbox_alert) !== String(ticket.inbox_alert)
      ) {
        return false;
      }

      // The Customer-Waiting / Agent-Waiting tabs ask who spoke last, which the
      // payload does answer — last_conversation comes with every ticket event.
      // Judging it here rather than declining means a customer message on a
      // ticket past the loaded window still surfaces on C.Waiting, which is the
      // one tab where waiting customers are supposed to be visible.
      const lastReply = this.$state._showTicketWithCustomerWaiting
        ? "customer"
        : this.$state._showTicketWithAgentWaiting
        ? "agent"
        : null;

      if (lastReply) {
        const type = (ticket.last_conversation as any)?.type;
        if (type === undefined || type === null) return false;

        const customerSide = [
          ConversationType.CUSTOMER,
          ConversationType.INFO,
        ].includes(type);
        const agentSide = [
          ConversationType.AGENT,
          ConversationType.CHANNEL,
        ].includes(type);

        if (lastReply === "customer" && !customerSide) return false;
        if (lastReply === "agent" && !agentSide) return false;
      }

      return true;
    },
    /**
     * Whether the active view can be judged from a ticket payload alone.
     *
     * Favourites, Has-Task and free-text search all live in data the socket does
     * not carry, so on those tabs an unloaded ticket is left out rather than
     * guessed at: the list stays short instead of showing a row that does not
     * belong. Rows already loaded still reorder normally.
     */
    canEvaluateFiltersLocally(): boolean {
      const filter = this._filterData as any;

      // Favourites, tasks, tags and free-text search all live in data a socket
      // payload does not carry, so membership cannot be judged here. On those
      // views an unloaded ticket is left out: the list stays short rather than
      // showing a row that does not belong.
      return !(
        this.$state._showTicketsWithFavorites ||
        this.$state._showTicketsWithTask ||
        filter?.search ||
        filter?.tags
      );
    },

    /**
     * The one place list order changes.
     *
     * A ticket gains activity — a message, or being handed to this agent — so it
     * belongs at the top. If it is already loaded it moves; if it is not, it is
     * inserted, which is the whole reason paging stays complete: a ticket whose
     * activity lifts it past the paging cursor would otherwise be in neither the
     * loaded rows nor any later page.
     */
    hoistTicket(ticket: Ticket, options: { force?: boolean } = {}) {
      if (!ticket?.id) return;

      const index = this._assignedTickets.findIndex(
        (t: Ticket) => t.id === ticket.id,
      );

      if (index === 0) {
        // Already top. Merge the fresher fields but change nothing structural, so
        // a busy conversation does not churn the list on every message.
        this._assignedTickets[0] = this.mergeTicket(
          this._assignedTickets[0],
          ticket,
        );
        return;
      }

      if (index > 0) {
        const merged = this.mergeTicket(this._assignedTickets[index], ticket);
        this._assignedTickets.splice(index, 1);
        this._assignedTickets.unshift(merged);
        this.$state._listPrependEpoch++;
        return;
      }

      // Not loaded — so this ticket has just moved above the paging cursor and no
      // later page will return it. Getting it on screen is the whole job.
      //
      // The filter is the agent's explicit choice about what belongs on screen, so
      // it applies to every insert — including `force` ones. `force` used to skip
      // this on the grounds that ticket_assigned is the authority on membership,
      // but membership in the *inbox* is not membership in the *filtered view*: a
      // ticket assigned while a label filter was on appeared in a list it did not
      // match, was ticked by "check all", and inflated the count against a working
      // set the server would never resolve it into.
      if (!this.doesTicketPassFilter(ticket)) return;

      // `force` still matters here. When the filter cannot be judged from a socket
      // payload at all (tags, search, favourites, tasks), a non-forced insert
      // stays out rather than guess — but a ticket the agent just picked, or was
      // just handed, has to stay reachable. Note this does NOT keep it out of the
      // filtered *selection*: a forced insert is recorded as an exclusion below, so
      // whether it is shown and whether it is acted on are decided separately.
      if (!options.force && !this.canEvaluateFiltersLocally()) return;

      // Draw it with whatever the payload carried. A card missing its customer
      // reads "Unknown" and fills itself in a moment later; a ticket that never
      // appears reads as nothing at all. Refusing to insert was the stricter and
      // far worse of the two.
      const cached = ticketsCache.get(ticket.id);
      const hydrated = ticket.owner ? ticket : cached ?? ticket;

      this.insertTicketAtTop(hydrated);

      if (!hydrated.owner) this.hydrateTicket(ticket.id);
    },

    /**
     * Fill in a ticket that was inserted from a payload too thin to draw fully.
     * Deduplicated, because a burst of events for the same ticket would otherwise
     * fire a request each.
     */
    async hydrateTicket(ticketId: number) {
      if (hydratingTicketIds.has(ticketId)) return;
      hydratingTicketIds.add(ticketId);

      try {
        const { response, error } = await getRequest<Ticket>(
          `desk/tickets/${ticketId}?with=automation`,
        );
        if (error || !response?.data) return;

        ticketsCache.set(ticketId, response.data);
        this.patchTicket(ticketId, response.data);
      } finally {
        hydratingTicketIds.delete(ticketId);
      }
    },

    /**
     * A ticket changed, but not in a way that means "new activity".
     *
     * The difference from hoistTicket is that a loaded row is left exactly where it
     * is. Correctness only ever requires the *insert*: a ticket outside the loaded
     * window has just moved above the paging cursor and no later page will return it.
     * A loaded ticket is already on screen, so nothing is lost by not moving it — and
     * moving it would mean the list reshuffles every time the agent opens a ticket or
     * a delivery receipt lands.
     */
    syncTicket(ticket: Ticket) {
      if (!ticket?.id) return;

      const isMine = ticket.agent_id === useAuthStore().user?.id;
      const isOpen = ticket.status === TicketStatus.OPEN;

      // Left this inbox — resolved, closed, unpicked, or handed to someone else.
      if (!isMine || !isOpen) {
        this.removeTicket(ticket.id);
        return;
      }

      const loaded =
        this._assignedTickets.some((t: Ticket) => t.id === ticket.id) ||
        this._pinnedTickets.some((t: Ticket) => t.id === ticket.id);

      if (loaded) {
        this.patchTicket(ticket.id, ticket);
        return;
      }

      if (!this.doesTicketPassFilter(ticket)) return;
      if (!this.canEvaluateFiltersLocally()) return;

      const hydrated = ticket.owner ? ticket : ticketsCache.get(ticket.id);
      if (!hydrated?.owner) return;

      this.insertTicketAtTop(hydrated);
    },

    /**
     * Put a not-yet-loaded ticket at the head of the list and keep the counts honest.
     *
     * It never joins the selection, whichever route it arrived by. A selection is a
     * set of ids resolved when the agent pressed "Check all"; a ticket that did not
     * exist in their inbox at that moment was not part of what they chose, and a
     * ticket that did is already in the set and renders ticked without help. So an
     * arriving row is drawn from the set, never added to it.
     */
    insertTicketAtTop(ticket: Ticket) {
      this._assignedTickets.unshift(this.mergeTicket({} as Ticket, ticket));

      if (this.$state._assignedTotal !== null) this.$state._assignedTotal++;
      this.setTabCount(
        this.$state._assignedTotal ?? this._assignedTickets.length,
      );
      this.$state._listPrependEpoch++;
    },

    /**
     * Broadcast payloads are not always complete — a conversation event carries a
     * lighter ticket than the list endpoint does. Spreading it raw would blank a
     * card that already had tags, so the relations only overwrite when present.
     */
    mergeTicket(existing: Ticket, incoming: Ticket): Ticket {
      return {
        ...existing,
        ...incoming,
        owner: incoming.owner ?? existing.owner,
        tags: incoming.tags ?? existing.tags,
        category: (incoming as any).category ?? (existing as any).category,
        has_favorited: incoming.has_favorited ?? existing.has_favorited,
      } as Ticket;
    },

    /** Update a loaded ticket's fields without touching list order. */
    patchTicket(ticketId: number, patch: Partial<Ticket>) {
      const apply = (list: Ticket[]) => {
        const index = list.findIndex((t: Ticket) => t.id === ticketId);
        if (index !== -1) list[index] = this.mergeTicket(list[index], patch);
      };

      apply(this._assignedTickets);
      // A pinned deep link renders from its own array, so it needs the patch too or
      // it would sit there showing stale tags and an old last message.
      apply(this.$state._pinnedTickets);
    },

    /**
     * The single removal path, so the counts and the scroll anchor stay honest.
     * Idempotent — a transfer delivers two events that both mean "remove", and the
     * acting tab has usually removed the row locally already.
     */
    removeTicket(ticketId: number) {
      // A pinned ticket (?other_ticket= / ?selected=) lives in its own array and
      // would otherwise stay on screen forever after being resolved or transferred.
      this.$state._pinnedTickets = this.$state._pinnedTickets.filter(
        (t: Ticket) => t.id !== ticketId,
      );

      // Drop it from the selection too. This is what keeps a selection that outlives
      // a tab change honest: a ticket resolved elsewhere, transferred away or
      // unpicked leaves the set on the same event that removes the row, so the count
      // follows reality instead of promising a ticket that is gone.
      this.$state._selectedIds.delete(ticketId);
      this.$state._selectionUnticked.delete(ticketId);

      if (this.$state._selectedIds.size === 0) {
        this.$state._selectionScopeLabel = "";
      }

      const index = this._assignedTickets.findIndex(
        (t: Ticket) => t.id === ticketId,
      );
      if (index === -1) return;

      this._assignedTickets.splice(index, 1);
      if (this.$state._assignedTotal !== null) {
        this.$state._assignedTotal = Math.max(
          0,
          this.$state._assignedTotal - 1,
        );
      }
      this.setTabCount(
        this.$state._assignedTotal ?? this._assignedTickets.length,
      );
      this.$state._listPrependEpoch++;
    },

    removeTickets(ticketIds: number[]) {
      ticketIds.forEach((id) => this.removeTicket(id));
    },

    addTicketToAssignedTickets(ticket: Ticket) {
      this.hoistTicket(ticket);
    },
    setShowTicketsWithFavorites(value: boolean) {
      this.$state._showTicketsWithFavorites = value;
    },
    removeConversationsForTicket(ticketId: number) {
      conversationsCache.remove(ticketId);
      pendingConversations.remove(ticketId);
    },

    /**
     * Throw away every cached thread and rebuild the open one.
     *
     * A cached thread is only kept correct by the socket events that extend it, and
     * setSelectedTicket serves it without revalidating for as long as the tab lives.
     * So after a drop a thread can be short with nothing on screen to say so — the
     * ticket list already offers a reload for exactly this reason, and the threads
     * need the same or the list gets repaired while the conversation pane does not.
     */
    async discardCachedThreads() {
      conversationsCache.clear();
      pendingConversations.clear();

      const selectedId = this._selectedTicket?.id;
      if (selectedId) await this.setSelectedTicket(selectedId);
    },
    setShowEditOrderForm(show: boolean) {
      this.$state._showEditOrderForm = show;
    },
    async setSelectedOrderForEdit(orderId: any) {
      const response = await getRequest<any>(`order-manager/orders/${orderId}`);
      this.$state._selectedOrderForEdit = response.response.data;
    },
  },

  getters: {
    showHistoryModal: (state) => state._showHistoryModal,
    showGalleryModal: (state) => state._showGalleryModal,
    selectedMessage: (state) => state._selectedMessage,
    selectedTicketCallHistory: (state) => state._selectedTicketCallHistory,
    assignedTickets: (state) => state._assignedTickets,
    ticketMessages: (state) => state._ticketMessages,
    selectedTicket: (state) => state._selectedTicket,
    currentContact: (state) => state._selectedTicket.owner,
    selectedTicketProfile: (state) => state._selectedTicketProfile,
    selectedTicketSourceEvent: (state) => state._selectedTicketSourceEvent,
    // The ad/shortlink a lead created from this ticket should carry: the
    // ticket's own referral when it has one, otherwise the newest referral
    // sitting on an info conversation — a customer returning through a
    // different ad leaves it there, never on the already-open ticket.
    selectedTicketReferral: (state): Referral | null => {
      const ticket = state._selectedTicket;

      if (ticket?.referral) {
        return ticket.referral;
      }

      return (
        (state._ticketMessages || [])
          .filter(
            (message) =>
              message.type === ConversationType.INFO && message.referral,
          )
          .sort(
            (a, b) =>
              new Date(b.original_created_at || b.created_at || 0).getTime() -
              new Date(a.original_created_at || a.created_at || 0).getTime(),
          )[0]?.referral ?? null
      );
    },
    showUploadDialog: (state) => state._showUploadDialog,
    showAddTagDialog: (state) => state._showAddTagDialog,
    showLeadParserDialog: (state) => state._showLeadParserDialog,
    showBrowseLinksDialog: (state) => state._showBrowseLinksDialog,
    privateReplyModalData: (state) => state._privateReplyModalData,
    showTicketWithCustomerWaiting: (state) =>
      state._showTicketWithCustomerWaiting,
    showEditOrderForm: (state) => state._showEditOrderForm,
    selectedOrderForEdit: (state) => state._selectedOrderForEdit,
    remarks: (state) => state._remarks,

    pinnedTickets: (state) => state._pinnedTickets,
    hasMoreAssignedTickets: (state) => state._assignedHasMore,
    assignedTicketsLoading: (state) => state._assignedLoading,
    assignedTicketsLoadingMore: (state) => state._assignedLoadingMore,
    // How many match the filter, not how many are on screen. null while searching,
    // where the server skips the count.
    assignedTicketsTotal: (state) => state._assignedTotal,
    // The one number every surface reads. It is `_selectedIds.size` and nothing
    // else — not a total minus exclusions, not the loaded row count — so the
    // header, the confirmation modal and the request cannot quote three different
    // figures the way they used to.
    selectedCount: (state) => state._selectedIds.size,
    isTicketSelected: (state) => (id: number) => state._selectedIds.has(id),
    selectionResolving: (state) => state._selectionResolving,
    selectionScope: (state) => state._selectionScopeLabel,
  },
});
